import React, { useState, useEffect } from "react";
import { Modal, Form, Typography, message, Card } from "antd";
import { MenuOutlined, DeleteOutlined } from "@ant-design/icons";
import Button from "../../../components/common/button";
import Dropdown from "../../../components/common/dropdown";
import LocationPicker from "../../../components/common/LocationPicker";
import { useSelector, useDispatch } from "react-redux";
import { fetchBusiness, reset } from "../../../store/slices/businessSlice";
import { getBusinessServices } from "../../../api/service";
import { saveTopDeals } from "../../../api/topDeals";
import colors from "../../../share/constants/constants";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableItem = ({ id, deal, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 mb-2 bg-white border border-inputFieldGrey rounded-xl"
    >
      <div className="flex items-center flex-1">
        <div
          {...attributes}
          {...listeners}
          className="cursor-move mr-3 text-gray-500 hover:text-primaryPurple"
        >
          <MenuOutlined />
        </div>
        <div className="flex-1">
          <div className="font-medium text-black">
            Rank {deal.rank}: {deal.vendorName}
          </div>
          <div className="text-sm text-gray-600">{deal.serviceName}</div>
        </div>
      </div>
      <button
        onClick={() => onRemove(deal.rank)}
        className="ml-3 text-red-500 hover:text-red-700"
      >
        <DeleteOutlined />
      </button>
    </div>
  );
};

const TopDealsForm = ({ open, onCancel, onSuccess, cityData, isEdit }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const businessNames = useSelector((state) => state?.business?.res?.items);
  const isBusinessLoading = useSelector((state) => state?.business?.isLoading);

  const [selectedCity, setSelectedCity] = useState("");
  const [locationCoordinates, setLocationCoordinates] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [selectBusinessId, setSelectBusinessId] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [isServiceLoading, setIsServiceLoading] = useState(false);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (open) {
      const payload = {
        pageNumber: 1,
        itemPerPage: 10,
      };
      dispatch(fetchBusiness(payload));
      if (isEdit && cityData) {
        setSelectedCity(cityData.city || "");
        setLocationName(cityData.city || "");
        if (cityData.coordinates) {
          setLocationCoordinates(cityData.coordinates);
        }
        if (cityData.deals) {
          setDeals(cityData.deals.map(deal => ({
            rank: deal.rank,
            vendorId: deal.vendorId?._id || deal.vendorId,
            vendorName: deal.vendorId?.businessName || deal.vendorName || "",
            serviceId: deal.serviceId?._id || deal.serviceId,
            serviceName: deal.serviceId?.serviceName || deal.serviceName || "",
          })));
        }
      }
    }
  }, [open, cityData, isEdit, dispatch]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setDeals([]);
      setSelectedCity("");
      setLocationCoordinates(null);
      setLocationName("");
      setSelectBusinessId(null);
      setSelectedBusiness(null);
      setServices([]);
      setSelectedService(null);
    }
  }, [open, form]);

  const loadMoreData = (page, itemPerPage = 10, text = "") => {
    const payload = {
      pageNumber: page,
      itemPerPage: itemPerPage,
    };
    if (text) {
      payload.text = text;
      payload.isLoadMore = true;
    }
    dispatch(fetchBusiness(payload));
  };

  const fetchBusinessByName = (payload) => {
    if (payload?.pageNumber === 1 && payload?.text) {
      dispatch(reset());
    }
    dispatch(fetchBusiness(payload));
  };

  const onDropDownClose = () => {
    dispatch(reset());
    const payload = {
      pageNumber: 1,
      itemPerPage: 10,
    };
    dispatch(fetchBusiness(payload));
  };

  const getServicesByBusinessId = async (businessId, page) => {
    setIsServiceLoading(true);
    const res = await getBusinessServices({
      pageNumber: page,
      pageSize: 50,
      businessId: businessId,
    });
    if (res) {
      setServices((prevServices) => [...prevServices, ...res?.items]);
    }
    setIsServiceLoading(false);
  };

  const handleBusinessChange = async (businessId) => {
    setSelectBusinessId(businessId);
    setSelectedBusiness((businessNames || []).find((b) => b._id === businessId));
    setSelectedService(null);
    setServices([]);
    form.setFieldsValue({ serviceId: undefined });
    await getServicesByBusinessId(businessId, 1);
  };

  const handleServiceChange = (serviceId) => {
    const service = services.find((s) => s._id === serviceId);
    setSelectedService(service);
  };

  const loadMoreServiceData = async (page) => {
    await getServicesByBusinessId(selectBusinessId, page);
  };

  const handleAddDeal = () => {
    if (!selectedService || !selectBusinessId) {
      message.warning("Please select a vendor and service");
      return;
    }

    if (!selectedCity || !locationCoordinates) {
      message.warning("Please select a city location on the map");
      return;
    }

    if (deals.length >= 5) {
      message.warning("Maximum 5 deals allowed");
      return;
    }

    const newRank = deals.length + 1;
    const newDeal = {
      rank: newRank,
      vendorId: selectBusinessId,
      vendorName: selectedBusiness?.businessName || "",
      serviceId: selectedService._id,
      serviceName: selectedService.serviceName || selectedService.name || "",
    };

    setDeals([...deals, newDeal]);
    setSelectedService(null);
    setSelectBusinessId(null);
    setSelectedBusiness(null);
    setServices([]);
    form.setFieldsValue({
      businessId: undefined,
      serviceId: undefined,
    });
  };

  const handleRemoveDeal = (rank) => {
    const updatedDeals = deals
      .filter((deal) => deal.rank !== rank)
      .map((deal, index) => ({
        ...deal,
        rank: index + 1,
      }));
    setDeals(updatedDeals);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = deals.findIndex((deal) => deal.rank === active.id);
      const newIndex = deals.findIndex((deal) => deal.rank === over.id);

      const reorderedDeals = arrayMove(deals, oldIndex, newIndex).map(
        (deal, index) => ({
          ...deal,
          rank: index + 1,
        })
      );

      setDeals(reorderedDeals);
    }
  };

  const saveTopDealsData = async () => {
    if (deals.length === 0) {
      message.warning("Please add at least one deal");
      return;
    }

    if (!selectedCity || !locationCoordinates) {
      message.warning("Please select a city location on the map");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        city: selectedCity,
        latitude: locationCoordinates.lat,
        longitude: locationCoordinates.lng,
        deals: deals.map((deal) => ({
          rank: deal.rank,
          vendorId: deal.vendorId,
          serviceId: deal.serviceId,
        })),
      };

      const res = await saveTopDeals(payload);
      if (res) {
        message.success("Top deals saved successfully");
        onSuccess && onSuccess();
        onCancel();
      }
    } catch (error) {
      message.error("Failed to save top deals");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit City Deals" : "Add New City Deals"}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <Card className="border-none">
        <Form form={form} layout="vertical" className="mt-1">
          <div className="sm:grid sm:grid-cols-2 gap-x-2 gap-y-1 px-2">
            <Form.Item
              label="City Location"
              className="sm:col-span-2"
              rules={[
                {
                  required: true,
                  message: "Please select a city location on the map",
                },
              ]}
            >
              <LocationPicker
                value={{
                  coordinates: locationCoordinates,
                  locationName: locationName,
                }}
                onChange={(coordinates) => {
                  setLocationCoordinates(coordinates);
                  form.setFieldValue("locationCoordinates", coordinates);
                }}
                onLocationNameChange={(name) => {
                  const cityName = name.split(",")[0].trim();
                  setSelectedCity(cityName);
                  setLocationName(name);
                  form.setFieldValue("city", cityName);
                  form.setFieldValue("locationName", name);
                }}
              />
            </Form.Item>

            <div className="sm:col-span-2 mt-4">
              <Typography.Title level={5} className="mt-0 mb-2">
                Select Vendor & Service
              </Typography.Title>
            </div>

            <Form.Item
              name="businessId"
              label="Vendor"
              rules={[
                {
                  required: true,
                  message: "This field is required",
                },
              ]}
            >
              <Dropdown
                className={"h-5 !rounded-lg"}
                placeholder="Choose Vendor"
                isBusiness={true}
                loadMoreData={(e, itemPerPage, text) => {
                  loadMoreData(e, itemPerPage, text);
                }}
                data={businessNames || []}
                onChange={handleBusinessChange}
                loader={isBusinessLoading}
                fetchData={fetchBusinessByName}
                onDropDownClose={onDropDownClose}
              />
            </Form.Item>

            <Form.Item
              name="serviceId"
              label="Service"
              rules={[
                {
                  required: true,
                  message: "This field is required",
                },
              ]}
            >
              <Dropdown
                className={"h-5 !rounded-lg"}
                placeholder="Choose Service"
                isService={true}
                loadMoreData={(e) => {
                  loadMoreServiceData(e);
                }}
                data={services}
                onChange={handleServiceChange}
                loader={isServiceLoading}
              />
            </Form.Item>

            <div className="sm:col-span-2 flex justify-end">
              <Button
                title="Add Service"
                color={colors.primaryPurple}
                className="w-auto px-4"
                onClick={handleAddDeal}
              />
            </div>

            {deals.length > 0 && (
              <div className="sm:col-span-2 mt-4">
                <Typography.Title level={5} className="mt-0 mb-2">
                  Top Deals (Drag to reorder)
                </Typography.Title>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={deals.map((deal) => deal.rank)}
                    strategy={verticalListSortingStrategy}
                  >
                    {deals.map((deal) => (
                      <SortableItem
                        key={deal.rank}
                        id={deal.rank}
                        deal={deal}
                        onRemove={handleRemoveDeal}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )}

            <div className="sm:col-span-2 flex justify-end mt-4">
              <Button
                title="Save Top Deals"
                color={colors.primaryPurple}
                className="w-auto px-4"
                onClick={saveTopDealsData}
                loading={loading}
                disabled={deals.length === 0}
              />
            </div>
          </div>
        </Form>
      </Card>
    </Modal>
  );
};

export default TopDealsForm;
